import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { X } from "lucide-react";

const COOKIE_KEY = "cookie_consent";

interface CookiePrefs {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState<CookiePrefs>({ necessary: true, analytics: false, marketing: false });

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_KEY);
    if (!stored) setVisible(true);
  }, []);

  const save = (p: CookiePrefs) => {
    localStorage.setItem(COOKIE_KEY, JSON.stringify(p));
    setVisible(false);
  };

  const acceptAll = () => save({ necessary: true, analytics: true, marketing: true });
  const rejectAll = () => save({ necessary: true, analytics: false, marketing: false });
  const saveCustom = () => save(prefs);

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4">
      <div className="mx-auto max-w-2xl rounded-xl border border-border bg-card p-6 shadow-lg">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display font-semibold text-sm">🍪 Configuración de cookies</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Usamos cookies para mejorar tu experiencia. Puedes personalizar tus preferencias o aceptar todas.
            </p>
          </div>
          <button type="button" onClick={rejectAll} aria-label="Cerrar"><X className="h-4 w-4 text-muted-foreground" /></button>
        </div>

        {showDetails && (
          <div className="mt-4 space-y-3 border-t border-border pt-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Necesarias</Label>
                <p className="text-xs text-muted-foreground">Imprescindibles para el funcionamiento del sitio</p>
              </div>
              <Switch checked disabled />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Analíticas</Label>
                <p className="text-xs text-muted-foreground">Nos ayudan a entender cómo usas el portal</p>
              </div>
              <Switch checked={prefs.analytics} onCheckedChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Marketing</Label>
                <p className="text-xs text-muted-foreground">Permiten mostrarte contenido personalizado</p>
              </div>
              <Switch checked={prefs.marketing} onCheckedChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))} />
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          {!showDetails && (
            <Button variant="outline" size="sm" onClick={() => setShowDetails(true)}>
              Personalizar
            </Button>
          )}
          {showDetails && (
            <Button variant="outline" size="sm" onClick={saveCustom}>
              Guardar preferencias
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={rejectAll}>Rechazar opcionales</Button>
          <Button size="sm" onClick={acceptAll}>Aceptar todas</Button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
